import { getCurrentUser } from '@modules/App/redux/user/actions';
import { User } from '@shared/interfaces/user';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { firebaseAuth } from '../../auth/firebase';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { clearAuthLocalStorageData, getRequestedPath, setSessionExpireAt } from '@shared/helpers/auth';
import { authUserSelector } from '@modules/App/redux/user/selectors';
import { useNavigate } from 'react-router-dom';
import { paths } from '@shared/enums/paths';
import httpNodeClient from '@shared/api/httpNodeClient';
import { HttpStatusCode } from '@shared/enums/HttpStatusCode';
import { getErrorInfo } from '@shared/helpers/getErrorInfo';

export const useAuth = (): { currentUser: User | null } => {
  const currentUser = useSelector(authUserSelector);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!currentUser) {
      dispatch(getCurrentUser());
    }
  }, [currentUser]);

  return {
    currentUser,
  };
};

export const useNavigateToUnauthorized = () => {
  const navigate = useNavigate();

  return useCallback((email: string, errorInfo: ReturnType<typeof getErrorInfo>) => {
    if (errorInfo?.code === HttpStatusCode.NotFound) {
      navigate(paths.userNotRegistered, { state: { email } });
    }
  }, []);
};

export const useSignIn = () => {
  const dispatch = useDispatch();
  const navigateToUnauthorized = useNavigateToUnauthorized();

  return useCallback(() => {
    if (firebaseAuth) {
      clearAuthLocalStorageData();
      signInWithPopup(firebaseAuth, new GoogleAuthProvider()).then(async (value) => {
        try {
          const res = await httpNodeClient.post('auth/login', {
            device: navigator.userAgent,
          });
          setSessionExpireAt(res.data.expiresAt);
          dispatch(getCurrentUser());
          window.location.href = (await getRequestedPath()) || paths.myProfile;
        } catch (error) {
          const errorInfo = getErrorInfo(error);
          navigateToUnauthorized(value.user.email || '', errorInfo);
        }
      });
    }
  }, []);
};
